import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { FrameworkClasses } from '../../../../framework/frameworkClasses';
import { getFrameworkDefaultValue } from '../../../../framework/frameworkInitialization';
import { electronics } from '../../../../utils/constants';
import { BeamBreakIcon } from '../../../Icons/Bits';
import { DistanceBasedSensorCheckContent, useDistanceBasedSensorCheck } from './DistanceBasedSensorCheck';
import { DEFAULT_THRESHOLD_M } from './distanceBasedSensorCheckUtils';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import { selectAreDrivebaseCommandActionsDisabled } from '../../../../redux/RobotSlice';

interface BeamBreakCheckDialogProps {
  open: boolean;
  port?: number;
  onClose: () => void;
}

const beamBreakDefaultThreshold = getFrameworkDefaultValue(
  undefined,
  FrameworkClasses.find((frameworkClass) => frameworkClass.name === 'BeamBreakSensor'),
  'detection_range'
);
const DEFAULT_BEAM_BREAK_THRESHOLD_M = typeof beamBreakDefaultThreshold === 'number' ? beamBreakDefaultThreshold : DEFAULT_THRESHOLD_M;
const BEAM_BREAK_THRESHOLD_DECIMAL_PLACES = 3;

const BeamBreakCheckDialog = ({ open, port, onClose }: BeamBreakCheckDialogProps) => {
  const { DETECTION_RANGES } = electronics.beamBreakSensor;
  const commandActionsDisabled = useAppSelector(selectAreDrivebaseCommandActionsDisabled);

  const { beamBroken, distance, thresholdMeters, setThresholdMeters } = useDistanceBasedSensorCheck({
    sensorType: 'beamBreak',
    port,
    open,
    disableWrites: commandActionsDisabled,
    detectionRanges: DETECTION_RANGES,
    defaultThresholdMeters: DEFAULT_BEAM_BREAK_THRESHOLD_M,
    thresholdDecimalPlaces: BEAM_BREAK_THRESHOLD_DECIMAL_PLACES,
    allowZeroThreshold: true,
    pollSensor: (p) => window.robotService?.getBeamBreakSensorSettings?.(p),
    setSensorSettings: (p, threshold) => window.robotService?.setBeamBreakSensorSettings?.(p, threshold),
  });

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
          <Box
            component={BeamBreakIcon}
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              display: 'block',
              color: 'primary.main',
              mr: 1,
            }}
          />
          <Box component="span" sx={{ ml: 0.5 }}>
            {port != null ? (
              <Trans i18nKey="beamBreakCheckDialog.titleWithPort" values={{ port }}>
                Port {{ port }}: Beam Break Sensor
              </Trans>
            ) : (
              <Trans i18nKey="beamBreakCheckDialog.title">Beam Break Sensor</Trans>
            )}
          </Box>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ width: 500, minHeight: 300 }}>
        <Stack spacing={2} alignItems="stretch">
          <Typography>
            <Trans i18nKey="beamBreakCheckDialog.main-text">
              Review the live output from your beam break sensor electronic to confirm functionality:
            </Trans>
          </Typography>
          <DistanceBasedSensorCheckContent
            beamBroken={beamBroken}
            distance={distance}
            thresholdMeters={thresholdMeters}
            disabled={commandActionsDisabled}
            onThresholdMetersChange={setThresholdMeters}
            onThresholdMetersCommitted={setThresholdMeters}
            showObjectDetected
            thresholdDecimalPlaces={BEAM_BREAK_THRESHOLD_DECIMAL_PLACES}
            brokenTrueI18nKey="beamBreakCheckDialog.broken-true"
            notBrokenFalseI18nKey="beamBreakCheckDialog.not-broken-false"
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" autoFocus onClick={handleClose}>
          <Trans i18nKey="beamBreakCheckDialog.button.close">Close</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default BeamBreakCheckDialog;
